import {cn} from '@tempmee/core'
import type {VariantProps} from 'class-variance-authority'
import {type HTMLAttributes, type ReactNode, forwardRef} from 'react'
import {BadgeVariantsFactory} from './Variants'

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof BadgeVariantsFactory> {
  /**
   * Semantic color role.
   * @default 'flagged'
   */
  severity?: NonNullable<VariantProps<typeof BadgeVariantsFactory>['severity']>
  /**
   * `counter` = a pill containing a count; `dot` = a bare 8px circle (no text).
   * @default 'counter'
   */
  variant?: NonNullable<VariantProps<typeof BadgeVariantsFactory>['variant']>
  /** Numeric count shown in `counter` mode. */
  count?: number
  /**
   * Counts above this are shown as `${max}+`.
   * @default 99
   */
  max?: number
  /** Optional content override for `counter` mode, replacing the rendered count. */
  children?: ReactNode
}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({className, severity, variant = 'counter', count, max = 99, children, ...props}, ref) => {
    const isCounter = variant === 'counter'
    const hasCount = count != null

    const content = isCounter
      ? (children ?? (hasCount ? (count > max ? `${max}+` : count) : null))
      : null

    return (
      <span
        aria-label={isCounter && hasCount ? `${count} notifications` : undefined}
        className={cn(BadgeVariantsFactory({severity, variant}), className)}
        ref={ref}
        // biome-ignore lint/a11y/useSemanticElements: role="status" is the correct live-region semantic for a notification badge; <output> implies a form calculation result
        role="status"
        {...props}
      >
        {content}
      </span>
    )
  },
)

Badge.displayName = 'Badge'
